import React, { useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Alert } from '@material-ui/lab'
import SydActionMenu from '../../../../../commonDesign/SydActionMenu'
import ChangeDatasourceSchemaDialog from '../../dialogs/ChangeDatasourceSchemaDialog'
import ChangeDatasourceCodeDialog from '../../dialogs/ChangeDatasourceCodeDialog'
import ModifyDatasourceFlagDialog from '../../dialogs/ModifyDatasourceFlagDialog'
import { dataCatalog } from '../../../../../../api/coreData'
import { usePolicy } from '../../../../../../hooks/usePolicy'

function DatasourceActionsMenu ({ datasource }) {
  const isInternalUser = usePolicy('admin_denali_internal')
  const changeDatasourceSchemaDialog = useRef(null)
  const changeDatasourceCodeDialog = useRef(null)
  const systemDialogRef = useRef(null)
  const internalDialogRef = useRef(null)
  const aiSettingsDialogRef = useRef(null)
  const alwaysAvailableDialogRef = useRef(null)
  const deprecatedDialogRef = useRef(null)

  const actions = useMemo(() => {
    const baseActions = [
      {
        key: 'change-schema',
        label: 'Change Schema',
        icon: 'database',
        onClick: (ds) => changeDatasourceSchemaDialog.current.open(ds)
      },
      {
        key: 'change-code',
        label: 'Change Code',
        icon: 'edit',
        onClick: (ds) => changeDatasourceCodeDialog.current.open(ds)
      }
    ]

    const internalActions = isInternalUser ? [
      {
        key: 'system',
        label: 'System',
        icon: 'storage',
        onClick: (ds) => systemDialogRef.current.open(ds)
      },
      {
        key: 'internal',
        label: 'Internal',
        icon: 'lock',
        onClick: (ds) => internalDialogRef.current.open(ds)
      }
    ] : []

    const otherActions = [
      {
        key: 'ai-settings',
        label: 'AI Settings',
        icon: 'sparkles',
        onClick: (ds) => aiSettingsDialogRef.current.open(ds)
      },
      {
        key: 'always-available',
        label: 'Always Available',
        icon: 'pin',
        onClick: (ds) => alwaysAvailableDialogRef.current.open(ds)
      },
      {
        key: 'deprecated',
        label: 'Deprecation Status',
        icon: 'archive',
        onClick: (ds) => deprecatedDialogRef.current.open(ds)
      }
    ]

    return [...baseActions, ...internalActions, ...otherActions]
  }, [isInternalUser])

  return (
    <>
      <SydActionMenu actions={actions} subject={datasource} />
      <ChangeDatasourceSchemaDialog ref={changeDatasourceSchemaDialog} />
      <ChangeDatasourceCodeDialog ref={changeDatasourceCodeDialog} />
      {isInternalUser && (
        <>
          <ModifyDatasourceFlagDialog
            ref={systemDialogRef}
            config={{
              title: 'System Settings',
              propertyName: 'isSystem',
              infoText: 'Control whether this datasource is marked as a system datasource.',
              getLabel: (value) => value ? 'System' : 'Not System',
              errorMessage: 'Something unexpected happened while updating the system status',
              mutationHook: () => dataCatalog.useModifyDatasourceSystem()
            }}
          />
          <ModifyDatasourceFlagDialog
            ref={internalDialogRef}
            config={{
              title: 'Internal Settings',
              propertyName: 'isInternal',
              infoText: 'Control whether this datasource is marked as internal.',
              getLabel: (value) => value ? 'Internal' : 'Not Internal',
              errorMessage: 'Something unexpected happened while updating the internal status',
              mutationHook: () => dataCatalog.useModifyDatasourceInternal()
            }}
          />
        </>
      )}
      <ModifyDatasourceFlagDialog
        ref={aiSettingsDialogRef}
        config={{
          title: 'AI Enabled Settings',
          propertyName: 'isAiEnabled',
          infoText: 'Control whether this datasource is available for AI-powered features.',
          getLabel: (value) => value ? 'AI Enabled' : 'AI Disabled',
          errorMessage: 'Something unexpected happened while updating the AI enabled status',
          mutationHook: () => dataCatalog.useModifyDatasourceAiEnabled()
        }}
      />
      <ModifyDatasourceFlagDialog
        ref={alwaysAvailableDialogRef}
        config={{
          title: 'Always Available Settings',
          propertyName: 'isAlwaysAvailable',
          infoText: 'When enabled, this datasource will always be available regardless of focus restrictions. When disabled, it will be subject to focus restrictions.',
          getLabel: (value) => value ? 'Always Available (Not Subject to Restrictions)' : 'Subject to Focus Restrictions',
          errorMessage: 'Something unexpected happened while updating the always available status',
          mutationHook: () => dataCatalog.useModifyDatasourceAlwaysAvailable()
        }}
      />
      <ModifyDatasourceFlagDialog
        ref={deprecatedDialogRef}
        config={{
          title: 'Deprecated Status',
          propertyName: 'isDeprecated',
          infoText: 'Mark this datasource as deprecated to indicate it should no longer be used.',
          getLabel: (value) => value ? 'Deprecated' : 'Active',
          errorMessage: 'Something unexpected happened while updating the deprecated status',
          mutationHook: () => dataCatalog.useModifyDatasourceDeprecated(),
          warningAlert: (
            <Alert severity='warning'>
              Marking this datasource as deprecated will signal to users that it should no longer be used for new implementations.
            </Alert>
          )
        }}
      />
    </>
  )
}

DatasourceActionsMenu.propTypes = {
  datasource: PropTypes.any
}

export default DatasourceActionsMenu
